import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TNewCustomField } from 'src/app/project/data-providers/api-providers/workspace-api-provider/workspace-requests.model';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { getErrorMessage } from 'src/app/project/helpers/database/get-error-message';
import { setChangedAccounts } from 'src/app/project/modules/account/account';
import { setChangedWorkspace } from 'src/app/project/modules/workspace/workspace';
import { AddCustomField } from 'src/app/project/modules/workspace/workspace.actions';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSettings,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { ECustomFieldType } from '../custom-field-types-enums';
import { TCustomField } from '../custom-fields.model';
import { CustomFieldsService } from '../custom-fields.service';

@Injectable({
  providedIn: 'root',
})
export class AddCustomFieldService {
  constructor(
    private store: Store,
    private translationPipe: TranslationPipe,
    private customFieldsService: CustomFieldsService,
    private promptService: PromptService,
    private modalService: ModalService,
  ) {}

  addCustomField(workspaceId: string, newFieldData: TNewCustomField): Observable<TCustomField> {
    return this.customFieldsService.addCustomField(workspaceId, newFieldData).pipe(
      tap((response) => {
        this.processNewCustomFieldResponse(response, workspaceId);
      }),
      catchError((error) => {
        this.handleAddCustomFieldError(error, newFieldData.type);

        return throwError(error);
      }),
    );
  }

  processNewCustomFieldResponse(response: TCustomField, workspaceId: string): void {
    const promptText = this.translationPipe.transform('prompt_field_created');
    const newCustomField = response;

    logEventInGTAG(EGoogleEventSettings.SETTINGS__CF__NEW, {
      source: EGoogleEventCategory.SETTINGS,
    });

    this.customFieldsService.addWorkspaceCustomField(workspaceId, newCustomField as TCustomField);

    this.store.dispatch(
      new AddCustomField({
        workspaceId,
        customFieldId: newCustomField.id.toString(),
      }),
    );

    setChangedWorkspace(workspaceId);
    setChangedAccounts(true);

    this.promptService.showSuccess(promptText);
    this.modalService.hideModal(false);
  }

  handleAddCustomFieldError(error: HttpErrorResponse, type: string): void {
    let promptText = this.translationPipe.transform('prompt_field_create_error');
    if (error.status === EStatusCode.BAD_REQUEST) {
      if (type === ECustomFieldType.FORMULA) {
        getErrorMessage(error).then((message) => {
          this.promptService.showError(message);
        });
        return;
      } else {
        promptText = this.translationPipe.transform('prompt_field_create_error_timeline_doubled');
      }
    } else if (error.status === EStatusCode.FORBIDDEN) {
      promptText = this.translationPipe.transform('prompt_field_create_error_timeline_not_enabled');
    }

    this.promptService.showError(promptText);
  }

  addListFieldFromExcel(
    workspaceId: string,
    file: File,
    listType: ECustomFieldType,
  ): Observable<TCustomField> {
    return this.customFieldsService.importListCustomField(workspaceId, file, listType).pipe(
      tap((response) => {
        this.processNewCustomFieldResponse(response, workspaceId);
      }),
    );
  }
}
